import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquare, Plus, Trash2, PanelLeftClose, PanelLeft, MoreHorizontal, Pencil } from 'lucide-react';

function ChatMenu({ anchorRef, onDelete, onRename, onClose }) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  // Position the menu relative to the anchor button
  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    // Place to the right of the dots button, vertically centered
    setPos({
      top: rect.bottom + 4,
      left: rect.right - 140, // menu width ~140px, align right edge to button
    });
  }, [anchorRef]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          anchorRef.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose, anchorRef]);

  if (!pos) return null;

  return createPortal(
    <div
      ref={menuRef}
      style={{ top: pos.top, left: pos.left }}
      className="fixed z-50 w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl py-1 animate-menu-in"
    >
      <button
        onClick={onRename}
        className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors rounded"
      >
        <Pencil className="w-3.5 h-3.5" />
        Rename
      </button>
      <button
        onClick={onDelete}
        className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded"
      >
        <Trash2 className="w-3.5 h-3.5" />
        Delete chat
      </button>
      <style>{`
        @keyframes menu-in {
          from { opacity: 0; transform: scale(0.95) translateY(-4px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-menu-in {
          animation: menu-in 0.15s ease-out both;
        }
      `}</style>
    </div>,
    document.body
  );
}

export default function ChatSidebar({ chats, chatId, onSelectChat, onNewChat, onDeleteChat, onRenameChat, isLoading }) {
  const [collapsed, setCollapsed] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef(null);
  const anchorRefs = useRef({});

  const getAnchorRef = useCallback((id) => {
    if (!anchorRefs.current[id]) {
      anchorRefs.current[id] = { current: null };
    }
    return anchorRefs.current[id];
  }, []);

  const startRenaming = useCallback((id, currentTitle) => {
    setMenuOpenId(null);
    setEditingId(id);
    setEditValue(currentTitle);
  }, []);

  const commitRename = useCallback(async () => {
    const trimmed = editValue.trim();
    if (editingId && trimmed && onRenameChat) {
      await onRenameChat(editingId, trimmed);
    }
    setEditingId(null);
    setEditValue('');
  }, [editingId, editValue, onRenameChat]);

  const cancelRename = useCallback(() => {
    setEditingId(null);
    setEditValue('');
  }, []);

  // Focus the input when editing starts
  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    setMenuOpenId(null);
    await onDeleteChat(id);
  };

  if (collapsed) {
    return (
      <div className="w-12 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex flex-col items-center py-3 gap-2">
        <button
          onClick={() => setCollapsed(false)}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          title="Expand sidebar"
        >
          <PanelLeft className="w-4 h-4" />
        </button>
        <button
          onClick={onNewChat}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          title="New chat"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="w-64 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Chats</h2>
        <div className="flex items-center gap-1">
          <button
            onClick={onNewChat}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            title="New chat"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCollapsed(true)}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            title="Collapse sidebar"
          >
            <PanelLeftClose className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Chat list */}
      <div className="flex-1 overflow-y-auto py-1">
        {isLoading ? (
          <div className="px-3 py-4 text-center">
            <div className="text-xs text-gray-400 dark:text-gray-500">Loading chats...</div>
          </div>
        ) : chats.length === 0 ? (
          <div className="px-3 py-8 text-center">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p className="text-xs text-gray-400 dark:text-gray-500">No chats yet</p>
          </div>
        ) : (
          chats.map(chat => (
            <div
              key={chat.id}
              onClick={() => editingId !== chat.id && onSelectChat(chat.id)}
              onDoubleClick={() => startRenaming(chat.id, chat.title)}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 group cursor-pointer transition-colors ${
                chatId === chat.id
                  ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
              }`}
            >
              <MessageSquare className={`w-3.5 h-3.5 flex-shrink-0 ${
                chatId === chat.id ? 'text-brand-500' : 'text-gray-400 dark:text-gray-500'
              }`} />

              {editingId === chat.id ? (
                <input
                  ref={editInputRef}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename();
                    if (e.key === 'Escape') cancelRename();
                  }}
                  onBlur={commitRename}
                  onClick={(e) => e.stopPropagation()}
                  className="text-sm flex-1 min-w-0 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-brand-500 text-gray-900 dark:text-gray-100"
                />
              ) : (
                <span className="text-sm truncate flex-1 min-w-0">{chat.title}</span>
              )}

              {editingId !== chat.id && (
                <button
                  ref={(el) => { getAnchorRef(chat.id).current = el; }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpenId(menuOpenId === chat.id ? null : chat.id);
                  }}
                  className={`w-6 h-6 flex items-center justify-center rounded text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex-shrink-0 ${
                    menuOpenId === chat.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              )}

              {menuOpenId === chat.id && (
                <ChatMenu
                  anchorRef={getAnchorRef(chat.id)}
                  onDelete={(e) => handleDelete(e, chat.id)}
                  onRename={() => startRenaming(chat.id, chat.title)}
                  onClose={() => setMenuOpenId(null)}
                />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
